/**
 * Loads everything the WeKIT™ Career Clarity 360 Passport needs and builds the model.
 *
 * Deliberately mirrors the load sequence in `AssessmentV2Result` step for
 * step — same bundle, same market read, same `composeLearner`, same
 * study-relaxation fallback, same top-15 cut. If these two ever drift, the
 * printed report would disagree with the screen the learner just read,
 * which is worse than having no report at all.
 */
import { useEffect, useState } from "react";
import { loadCareersBundle } from "@/data/careersBundle";
import { bundleToCareers } from "@/lib/careersToScoring";
import { loadMarketIntelligence } from "@/lib/marketIntelligence";
import { matchCareers, effectiveCapacity, entryCost } from "@/lib/scoring";
import { supabase } from "@/integrations/supabase/client";
import { ARCHETYPES, emptyVector, type ArchetypeVector } from "./archetypes";
import { passionArchetypeNudge } from "./passions";
import { dreamArchetypeNudge, pullArchetypeNudge } from "./careerPull";
import { v1PortArchetypeNudge } from "./v1port/nudge";
import { composeLearner, type AssessmentV2Snapshot } from "./results";
import { buildDossier, type Dossier } from "./dossier";
import type { SlotState } from "./slots";
import type { Band } from "./band";

/**
 * The tally the learner is actually scored on: revealed decisions plus the
 * bounded stated-interest nudge. Recomputed here rather than exported from
 * `composeLearner` so the dossier's bars can never disagree with the
 * primary archetype printed above them.
 */
function effectiveTally(state: AssessmentV2Snapshot): ArchetypeVector {
  const revealed = (state.part2.archetype_tally as ArchetypeVector) ?? emptyVector();
  const nudge = passionArchetypeNudge(state.part1.passion_ids);
  const dream = dreamArchetypeNudge(state.part1.childhood_dream);
  const pull = pullArchetypeNudge(state.part1.career_pull_ids);
  // Same bounded channel composeLearner uses, so the bars can't disagree with
  // the primary archetype printed above them.
  const ported = v1PortArchetypeNudge(state.part2);
  const out = { ...revealed } as ArchetypeVector;
  for (const a of ARCHETYPES) {
    out[a] = (out[a] ?? 0) + (nudge[a] ?? 0) + (dream[a] ?? 0)
      + (pull[a] ?? 0) + (ported[a] ?? 0);
  }
  return out;
}

async function learnerName(): Promise<string | null> {
  try {
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth.user?.id;
    if (!uid) return null;
    const { data } = await supabase
      .from("profiles")
      .select("full_name")
      .eq("id", uid)
      .maybeSingle();
    const name = (data as { full_name?: string | null } | null)?.full_name;
    return name?.trim() || null;
  } catch {
    return null;
  }
}

export interface UseDossierResult {
  dossier: Dossier | null;
  error: string | null;
}

export function useDossier(
  state: AssessmentV2Snapshot,
  band: Band,
  meta?: { reportHash?: string | null; attemptId?: string | null },
): UseDossierResult {
  const [dossier, setDossier] = useState<Dossier | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reportHash = meta?.reportHash ?? null;
  const attemptId = meta?.attemptId ?? null;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [bundle, market, name] = await Promise.all([
          loadCareersBundle(),
          loadMarketIntelligence(),
          learnerName(),
        ]);
        const careers = bundleToCareers(bundle, (id) => market.get(id));

        let learner = composeLearner(state);
        let out = matchCareers(learner, careers, 15);
        let studyRelaxed = false;

        // Same graceful degradation as the result screen. Eligibility must
        // never return empty.
        if (out.results.length < 3 && learner.studyCapacity !== "degree") {
          const nextCap = learner.studyCapacity === "none" ? "short"
            : learner.studyCapacity === "short" ? "parttime" : "degree";
          learner = { ...learner, studyCapacity: nextCap };
          out = matchCareers(learner, careers, 15);
          studyRelaxed = true;
        }

        const cap = effectiveCapacity(learner);
        const ruledOutByStudy = careers.reduce(
          (n, c) => (entryCost(c) > cap ? n + 1 : n), 0,
        );

        if (cancelled) return;
        setDossier(buildDossier({
          band,
          part1: state.part1,
          slots: state.part2.slots as SlotState | undefined,
          learner,
          tally: effectiveTally(state),
          results: out.results,
          careers,
          totalConsidered: careers.length,
          eligiblePool: out.eligiblePool,
          ruledOutByStudy,
          studyRelaxed,
          learnerName: name,
          generatedAt: new Date(),
          reportHash,
          attemptId,
        }));
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Your report didn't load.");
      }
    })();
    return () => { cancelled = true; };
  }, [state, band, reportHash, attemptId]);

  return { dossier, error };
}
